import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Blockquote, Button, Card, ListGroup } from 'flowbite-react';
import { useEffect, useState } from 'react';
import { useLoaderData } from 'react-router-dom';

import { LoaderData, loaders } from 'src/api/loaders';
import { mutations } from 'src/api/mutations';
import { queries } from 'src/api/queries';

import { UseCaseHeader } from './video-flow';

export function DashboardVideoFlowGenerateScript() {
  const queryClient = useQueryClient();
  const loaderUsecase = useLoaderData() as LoaderData<typeof loaders.usecaseLoader>;
  const [generating, setGenerating] = useState(false);
  const [gptResponse, setGptResponse] = useState('');
  const [currentSelected, setCurrentSelected] = useState(-1);
  const { data: savedGeneratedScripts } = useQuery({
    ...queries.savedVideoScripts(loaderUsecase.id),
    enabled: !!loaderUsecase,
  });

  useEffect(() => {
    if (savedGeneratedScripts?.results.length) {
      setCurrentSelected(0);
    }
  }, [savedGeneratedScripts]);

  const saveScript = useMutation({
    ...mutations.saveVideoScript(),
    onSuccess: async (data) => {
      queryClient.setQueryData(
        ['usecase', loaderUsecase.id, 'get-saved-video-scripts'],
        (oldData: typeof savedGeneratedScripts) => {
          if (oldData) {
            const newData = { ...oldData };
            newData.results.push(data);
            return newData;
          }
          return { results: [data] };
        },
      );
    },
  });

  const generateResponse = async () => {
    setCurrentSelected(-1);
    setGptResponse('');
    setGenerating(true);
    const response = await fetch(`/mapi/usecases/${loaderUsecase.id}/generate_video_script/`);
    if (!response.body || response.status != 200) {
      console.log('failed to fetch response', response);
      setGenerating(false);
      const defaultErrorMessage = 'Something went wrong! Please reach out for help.';
      try {
        const responseJson = await response.json();
        setGptResponse(responseJson.error ?? defaultErrorMessage);
      } catch {
        setGptResponse(defaultErrorMessage);
      }
      return;
    }
    let responseText = '';
    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    for (;;) {
      const { value, done } = await reader.read();
      if (done) {
        await saveScript.mutateAsync([loaderUsecase.id, responseText]);
        setGenerating(false);
        setCurrentSelected(0);
        break;
      }
      responseText += value;
      setGptResponse(responseText);
    }
  };

  return (
    <div className='flex w-full flex-col gap-6'>
      <UseCaseHeader usecase={loaderUsecase} badgeText='Generate Script' badgeColor='purple' />
      <div className='flex h-full flex-row gap-6'>
        <Card className='h-full	w-1/4' theme={{ root: { children: 'justify-start p-0' } }}>
          <ListGroup theme={{ root: { base: 'rounded-none' } }}>
            {savedGeneratedScripts !== undefined &&
              savedGeneratedScripts.results.length !== 0 &&
              savedGeneratedScripts.results.map((_, idx) => (
                <ListGroup.Item
                  theme={{ base: 'rounded-none' }}
                  key={idx}
                  href='#'
                  active={currentSelected === idx}
                  onClick={() => setCurrentSelected(idx)}
                >
                  <div className='py-2'>Script - {savedGeneratedScripts.results.length - idx}</div>
                </ListGroup.Item>
              ))}
            {savedGeneratedScripts === undefined || savedGeneratedScripts.results.length !== 0 || (
              <ListGroup.Item theme={{ base: 'rounded-none' }}>
                <div className='py-2'>New Script</div>
              </ListGroup.Item>
            )}
          </ListGroup>
        </Card>
        <Card className='h-full w-3/4'>
          <div>
            <p>
              Click the button below to generate a draft script for your video that you can use as
              inspiration for what to say. The script is generated using the magic of AI and
              automatically incorporates details about your brand and the particular flow you are
              recording a video for!
            </p>
          </div>
          <Blockquote className='text-md my-4 whitespace-pre-line border-l-4 border-gray-300 bg-gray-50 p-4	font-normal'>
            {currentSelected >= 0 &&
              savedGeneratedScripts?.results[
                savedGeneratedScripts.results.length - currentSelected - 1
              ]?.script}
            {currentSelected == -1 && Boolean(gptResponse.length) && gptResponse}
            {currentSelected == -1 && !gptResponse.length && Boolean(generating) && 'Generating...'}
            {currentSelected == -1 &&
              !gptResponse.length &&
              !generating &&
              'Press button to generate'}
          </Blockquote>
          <div className='mt-auto w-56 self-end'>
            <Button
              className='w-full'
              onClick={generateResponse}
              disabled={generating}
              outline
              gradientDuoTone='pinkToOrange'
              isProcessing={generating}
            >
              <span className='inline-flex h-6 items-center'>
                {Boolean(generating) && <span>Generating with AI</span>}
                {Boolean(generating) || (
                  <>
                    <span className='pr-2 text-2xl'>✨ </span>
                    <span>Generate {currentSelected !== -1 && 'new'} with AI</span>
                  </>
                )}
              </span>
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
